// Seeding the add-question drawer from the URL.
//
// Kept in its own tiny module rather than beside the drawer or beside the warnings that link to it:
// both the compliance page and the event dashboard need `birthdayQuestionUrl`, and importing it
// from either of those would drag a whole page's bundle (recharts, QR codes, a drawer) along for
// one string. The URL builder and the shape the drawer reads live together so they cannot drift.

/**
 * Fields to seed a NEW question with, read from the URL (…/questions/add?personField=birthday).
 *
 * A host sent here to fix one specific problem — "this event has no date-of-birth question, so your
 * waiver can't be signed" — should land on a form that already IS that question, rather than on a
 * blank one plus instructions they then have to follow correctly. Getting the person-field link
 * wrong is exactly what caused the problem they were sent to fix.
 */
export interface QuestionPrefill {
  /** A `PERSON_FIELDS` value, e.g. 'birthday'. Drives the value type too. */
  personField?: string | null;
  title?: string | null;
  isRequired?: boolean;
  /**
   * A WHOLE ready-made question (the create wizard's starters — `EVENT_QUESTION_STARTERS`). Each is optional; an absent
   * one keeps the blank form's default. ⚠ `valueType` is still ignored when a `personField` is given — the field decides.
   */
  questionType?: string | null;
  valueType?: string | null;
  options?: string[] | null;
  /** WHO must answer — wins over `isRequired` when both are given. */
  requiredScope?: string | null;
  helpText?: string | null;
  askOncePerGroup?: boolean;
}

/**
 * The add-question drawer, pre-filled to create the question linked to one PERSON FIELD.
 *
 * ⚠ No `valueType` param. The drawer DERIVES it from the person field, because a person field only
 * accepts certain value types and its own change handler drops a field that doesn't match — a
 * mismatched pair passed by hand would silently produce a question with no person link, which is
 * the very state these warnings exist to fix.
 */
export const personFieldQuestionUrl = (
  eventId: string,
  personField: string,
  title: string,
  isRequired = false,
): string => {
  const params = new URLSearchParams({ personField, title });
  if (isRequired) {
    params.set('required', '1');
  }

  return `/events/${eventId}/registration/questions/add?${params.toString()}`;
};

/**
 * "Add the question that unblocks my waivers" — a date question linked to the person's birthday.
 *
 * ⚠ Required on purpose: an optional date of birth left blank still leaves the age unknown, so the
 * waiver would still be unsignable and the host would think they had fixed it. They can untick it
 * in the drawer if they mean something else by it.
 */
export const BIRTHDAY_QUESTION_PREFILL = { personField: 'birthday', title: 'Date of birth', isRequired: true } as const satisfies QuestionPrefill;

/** The same question as a URL to the Questions page's add drawer. The wizard's Policies step passes the prefill itself. */
export const birthdayQuestionUrl = (eventId: string): string =>
  personFieldQuestionUrl(eventId, BIRTHDAY_QUESTION_PREFILL.personField, BIRTHDAY_QUESTION_PREFILL.title, BIRTHDAY_QUESTION_PREFILL.isRequired);

/**
 * "Add somewhere to send the signing link to" — an email question linked to the person's email.
 *
 * Required, for the same reason as the birthday: an adult signs their own waiver via an emailed
 * link, and a blank address means no link is ever sent. Phone would do as well (the drawer's person
 * field can be changed to it), but email is what the signing link actually uses today.
 */
export const emailQuestionUrl = (eventId: string): string =>
  personFieldQuestionUrl(eventId, 'email', 'Email', true);
